<template>
  <div class="dashboard-container">
    <el-row :gutter="20">
      <el-col :span="24">
        <div class="welcome-card">
          <h2>欢迎使用管理后台</h2>
          <p>今天是 {{ todayDate }}，祝您工作愉快！</p>
        </div>
      </el-col>
    </el-row>

    <el-row :gutter="20" class="statistics-row">
      <el-col :span="4">
        <el-card shadow="hover" class="stat-card">
          <router-link to="/admin/orders/in-transit" class="stat-link">
            <div class="stat-header">
              <i class="el-icon-truck stat-icon"></i>
              <span class="stat-title">在途订单</span>
            </div>
            <div class="stat-value">{{ orderStats.inTransit }}</div>
          </router-link>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card shadow="hover" class="stat-card">
          <router-link to="/admin/orders/testing" class="stat-link">
            <div class="stat-header">
              <i class="el-icon-loading stat-icon"></i>
              <span class="stat-title">测试中订单</span>
            </div>
            <div class="stat-value">{{ orderStats.testing }}</div>
          </router-link>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card shadow="hover" class="stat-card">
          <router-link to="/admin/orders/completed" class="stat-link">
            <div class="stat-header">
              <i class="el-icon-circle-check stat-icon"></i>
              <span class="stat-title">已完成订单</span>
            </div>
            <div class="stat-value">{{ orderStats.completed }}</div>
          </router-link>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card shadow="hover" class="stat-card">
          <router-link to="/admin/orders/expiring" class="stat-link">
            <div class="stat-header">
              <i class="el-icon-time stat-icon"></i>
              <span class="stat-title">超期订单</span>
            </div>
            <div class="stat-value">{{ orderStats.expiring }}</div>
          </router-link>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card shadow="hover" class="stat-card">
          <router-link to="/admin/orders/dispute" class="stat-link">
            <div class="stat-header">
              <i class="el-icon-warning stat-icon"></i>
              <span class="stat-title">争议/复测订单</span>
            </div>
            <div class="stat-value">{{ orderStats.dispute }}</div>
          </router-link>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card shadow="hover" class="stat-card">
          <router-link to="/admin/orders/unsettled" class="stat-link">
            <div class="stat-header">
              <i class="el-icon-wallet stat-icon"></i>
              <span class="stat-title">未结算订单</span>
            </div>
            <div class="stat-value">{{ orderStats.unsettled }}</div>
          </router-link>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="20" class="statistics-row">
      <el-col :span="8">
        <el-card shadow="hover" class="amount-card">
          <div class="amount-title">总订单金额</div>
          <div class="amount-value">¥{{ formatNumber(orderAmount.total) }}</div>
          <div class="amount-footer">
            <el-progress :percentage="100" :show-text="false" :stroke-width="8" status="success"></el-progress>
          </div>
        </el-card>
      </el-col>
      <el-col :span="8">
        <el-card shadow="hover" class="amount-card">
          <div class="amount-title">未结算订单金额</div>
          <div class="amount-value">¥{{ formatNumber(orderAmount.unsettled) }}</div>
          <div class="amount-footer">
            <el-progress :percentage="orderAmount.unsettledPercentage" :show-text="false" :stroke-width="8" status="warning"></el-progress>
            <div class="amount-desc">占总金额的 {{ orderAmount.unsettledPercentage }}%</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="8">
        <el-card shadow="hover" class="amount-card">
          <div class="amount-title">本月未结算金额</div>
          <div class="amount-value">¥{{ formatNumber(orderAmount.thisMonthUnsettled) }}</div>
          <div class="amount-footer">
            <el-progress :percentage="orderAmount.thisMonthPercentage" :show-text="false" :stroke-width="8" color="#409EFF"></el-progress>
            <div class="amount-desc">占未结算金额的 {{ orderAmount.thisMonthPercentage }}%</div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="20">
      <el-col :span="24">
        <el-card class="list-card">
          <div slot="header" class="list-header">
            <span>即将/已超期订单</span>
            <router-link to="/admin/orders/expiring" class="view-more">
              查看全部 <i class="el-icon-arrow-right"></i>
            </router-link>
          </div>
          <div v-if="expiringOrders.length === 0" class="empty-list">
            <i class="el-icon-circle-check empty-icon"></i>
            <span>暂无超期订单</span>
          </div>
          <el-table v-else :data="expiringOrders" style="width: 100%" size="mini">
            <el-table-column width="100" label="订单号" prop="id"></el-table-column>
            <el-table-column label="客户" prop="customer"></el-table-column>
            <el-table-column label="商家" prop="merchant"></el-table-column>
            <el-table-column label="预计完成时间" width="110">
              <template slot-scope="scope">
                {{ formatDate(scope.row.expectedEndTime) }}
              </template>
            </el-table-column>
            <el-table-column label="超期情况" width="100">
              <template slot-scope="scope">
                <el-tag size="mini" :type="scope.row.daysInfo && scope.row.daysInfo.includes('已超期') ? 'danger' : 'warning'">
                  {{ scope.row.daysInfo || scope.row.status }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column width="80" label="状态">
              <template slot-scope="scope">
                <el-tag size="mini" type="info">{{ scope.row.orderStatusName }}</el-tag>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="20">
      <el-col :span="24">
        <el-card class="list-card">
          <div slot="header" class="list-header">
            <span>未结算订单</span>
            <router-link to="/admin/orders/unsettled" class="view-more">
              查看全部 <i class="el-icon-arrow-right"></i>
            </router-link>
          </div>
          <div v-if="unsettledOrders.length === 0" class="empty-list">
            <i class="el-icon-circle-check empty-icon"></i>
            <span>暂无未结算订单</span>
          </div>
          <el-table v-else :data="unsettledOrders" style="width: 100%" size="mini">
            <el-table-column width="100" label="订单号" prop="id"></el-table-column>
            <el-table-column label="客户" prop="customer"></el-table-column>
            <el-table-column label="商家" prop="merchant"></el-table-column>
            <el-table-column width="100" label="金额" prop="amount" sortable :sort-method="sortByAmount"></el-table-column>
          </el-table>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="20">
      <el-col :span="24">
        <el-card class="list-card">
          <div slot="header" class="list-header">
            <span>复测/争议订单</span>
            <router-link to="/admin/orders/dispute" class="view-more">
              查看全部 <i class="el-icon-arrow-right"></i>
            </router-link>
          </div>
          <div v-if="disputeOrders.length === 0" class="empty-list">
            <i class="el-icon-circle-check empty-icon"></i>
            <span>暂无复测/争议订单</span>
          </div>
          <el-table v-else :data="disputeOrders" style="width: 100%" size="mini">
            <el-table-column width="100" label="订单号" prop="id"></el-table-column>
            <el-table-column label="客户" prop="customer"></el-table-column>
            <el-table-column label="商家" prop="merchant"></el-table-column>
            <el-table-column label="类型" width="80">
              <template slot-scope="scope">
                <el-tag size="mini" :type="scope.row.orderType === '争议订单' ? 'danger' : 'warning'">
                  {{ scope.row.orderType }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="仪器名称" prop="deviceName"></el-table-column>
            <el-table-column label="仪器型号" prop="deviceModel"></el-table-column>
            <el-table-column width="80" label="状态">
              <template slot-scope="scope">
                <el-tag size="mini" type="info">{{ scope.row.status }}</el-tag>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { AdminApi } from '@/api/adminApi'

export default {
  name: 'AdminDashboard',
  data() {
    return {
      todayDate: new Date().toLocaleDateString('zh-CN', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric', 
        weekday: 'long' 
      }),
      loading: false,
      // 订单状态统计
      orderStats: {
        inTransit: 0,
        testing: 0,
        completed: 0,
        expiring: 0,
        dispute: 0,
        unsettled: 0
      },
      // 订单金额统计
      orderAmount: {
        total: 0,
        unsettled: 0,
        thisMonthUnsettled: 0,
        unsettledPercentage: 0,
        thisMonthPercentage: 0
      },
      // 即将/已超期订单列表
      expiringOrders: [],
      // 未结算订单列表
      unsettledOrders: [],
      // 复测/争议订单列表
      disputeOrders: []
    }
  },
  created() {
    this.fetchDashboardData()
  },
  methods: {
    formatNumber(num) {
      if (num === undefined || num === null) return "0";
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    },
    sortByAmount(a, b) {
      // 使用amountValue进行排序
      const valueA = a.amountValue || 0;
      const valueB = b.amountValue || 0;
      return valueB - valueA; // 默认降序排列（大金额在前）
    },
    async fetchDashboardData() {
      this.loading = true
      try {
        const adminApi = new AdminApi()
        const response = await adminApi.getDashboardData()
        
        console.log('仪表盘数据原始返回:', response)
        
        // 直接使用返回的数据，不需要再从data中提取
        // 因为_get方法已经将response.data.data提取出来了
        const data = response
        
        console.log('处理后的仪表盘数据:', {
          orderStats: data.orderStats,
          orderAmount: data.orderAmount,
          expiringOrders: data.expiringOrders,
          unsettledOrders: data.unsettledOrders,
          disputeOrders: data.disputeOrders
        })
        
        // 检查每个对象是否正确
        console.log('orderStats是否存在:', !!data.orderStats, typeof data.orderStats)
        console.log('orderAmount是否存在:', !!data.orderAmount, typeof data.orderAmount)
        console.log('disputeOrders是否存在:', !!data.disputeOrders, data.disputeOrders?.length)
        
        // 更新订单状态统计
        if (data.orderStats) {
          this.orderStats = data.orderStats
        }
        
        // 更新订单金额统计
        if (data.orderAmount) {
          this.orderAmount = data.orderAmount
        }
        
        // 更新即将/已超期订单列表
        if (Array.isArray(data.expiringOrders)) {
          this.expiringOrders = data.expiringOrders
        }
        
        // 更新未结算订单列表
        if (Array.isArray(data.unsettledOrders)) {
          this.unsettledOrders = data.unsettledOrders
        }
        
        // 更新复测/争议订单列表
        if (Array.isArray(data.disputeOrders)) {
          this.disputeOrders = data.disputeOrders
        }
        
        // 打印最终的状态数据
        console.log('Dashboard组件数据更新后:', {
          orderStats: this.orderStats,
          orderAmount: this.orderAmount,
          expiringOrders: this.expiringOrders,
          unsettledOrders: this.unsettledOrders,
          disputeOrders: this.disputeOrders
        })
      } catch (error) {
        console.error('获取仪表盘数据失败:', error)
        this.$message.error('获取仪表盘数据失败，请刷新页面重试')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.dashboard-container {
  padding: 20px;
}

/* 欢迎卡片样式 */
.welcome-card {
  background: linear-gradient(to right, #1890ff, #36cfc9);
  padding: 20px;
  border-radius: 4px;
  color: white;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.welcome-card h2 {
  margin: 0;
  font-size: 24px;
}

.welcome-card p {
  margin-top: 10px;
  font-size: 14px;
  opacity: 0.8;
}

/* 统计卡片行样式 */
.statistics-row {
  margin-bottom: 20px;
}

/* 订单数量统计卡片样式 */
.stat-card {
  height: 120px;
  overflow: hidden;
  transition: all 0.3s;
  cursor: pointer;
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.stat-link {
  display: block;
  height: 100%;
  text-decoration: none;
  color: inherit;
}

.stat-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 10px;
}

.stat-icon {
  font-size: 30px;
  margin-bottom: 5px;
  color: #409EFF;
}

.stat-title {
  font-size: 14px;
  color: #606266;
}

.stat-value {
  text-align: center;
  font-size: 28px;
  font-weight: bold;
  color: #303133;
}

/* 金额统计卡片样式 */
.amount-card {
  padding: 15px;
  height: 140px;
}

.amount-title {
  font-size: 15px;
  color: #909399;
  margin-bottom: 10px;
}

.amount-value {
  font-size: 26px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 20px;
}

.amount-footer {
  margin-top: 10px;
}

.amount-desc {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
  text-align: right;
}

/* 列表卡片样式 */
.list-card {
  margin-bottom: 20px;
  height: 265px;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.view-more {
  font-size: 12px;
  color: #409EFF;
  text-decoration: none;
}

.empty-list {
  height: 150px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #909399;
}

.empty-icon {
  font-size: 40px;
  margin-bottom: 10px;
  color: #DCDFE6;
}
</style> 